import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, Download, Trash2, Lock, Loader2, Archive } from 'lucide-react';
import { followZip, zipLine, ZIP_STARTED } from '@/lib/finalFiles';

const ALLOWED_EXTS = ['.lrcat', '.lrtemplate', '.xmp', '.dng', '.cr2', '.cr3', '.nef',
  '.arw', '.raf', '.orf', '.rw2', '.tif', '.tiff', '.psd', '.zip'];

const extOf = (name) => '.' + (name.split('.').pop() || '').toLowerCase();

export const PhotoDeliverables = ({ project, onRefresh }) => {
  const { user } = useAuth();
  const isUploader = ['admin', 'staff', 'editor'].includes(user?.role);
  const isClient = user?.role === 'client';
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  // Bulk download rides the SAME ZIP job as video deliverables — for a photo
  // project the review-video list is empty, so the archive is exactly this
  // section's files. Poll ref cleared on unmount so a slow job can't set state
  // on a dead component.
  const [zipJob, setZipJob] = useState(null);
  // The Download All run being followed (lib/finalFiles.followZip). Another run, or leaving the page, ends it.
  const zipRun = useRef(0);
  useEffect(() => () => { zipRun.current = -1; }, []);

  // Source deliverables live on versions[].files (type project_file). get_project already
  // filters versions to released-only for clients, so a client sees these ONLY after approval.
  const deliverables = [];
  const seen = new Set();
  for (const v of (project.versions || [])) {
    for (const f of (v.files || [])) {
      if (f.type === 'project_file' && f.file_id && !seen.has(f.file_id)) {
        seen.add(f.file_id);
        deliverables.push(f);
      }
    }
  }

  if (!isUploader && !isClient) return null;
  if (isClient && deliverables.length === 0) return null;

  const clientLocked = isClient && !project.client_approved;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    const bad = files.filter((f) => !ALLOWED_EXTS.includes(extOf(f.name)));
    if (bad.length) { toast.error(`Unsupported file type: ${bad.map((f) => f.name).join(', ')}`); return; }
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const presign = await api.post('/storage/presign-upload', {
          filename: f.name, scope: 'project_deliverable', project_id: project.id,
          version: '1', content_type: f.type || 'application/octet-stream',
        });
        if (presign.data.upload_url && presign.data.upload_url !== 'LOCAL') {
          // fetch() never throws on 4xx/5xx — check res.ok so a failed PUT isn't
          // confirmed as an empty "completed" deliverable.
          const putRes = await fetch(presign.data.upload_url, { method: 'PUT', body: f, headers: { 'Content-Type': f.type || 'application/octet-stream' } });
          if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
        } else {
          const form = new FormData(); form.append('file', f);
          await api.post(`/storage/upload/${presign.data.file_id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        await api.post(`/storage/confirm-upload/${presign.data.file_id}`);
        uploaded.push({ file_id: presign.data.file_id, filename: f.name, key: presign.data.key });
      }
      await api.post(`/projects/${project.id}/photo-deliverables`, { files: uploaded });
      toast.success(`${uploaded.length} deliverable file${uploaded.length > 1 ? 's' : ''} added`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDelete = async (fid) => {
    try { await api.delete(`/projects/${project.id}/photo-deliverables/${fid}`); toast.success('Removed'); onRefresh && onRefresh(); }
    catch { toast.error('Failed to remove'); }
  };

  const handleDownload = async (f) => {
    try {
      const r = await api.post('/storage/presign-download', { key: f.key });
      if (r.data.download_url) window.location.href = r.data.download_url;
      else toast.error('Download unavailable');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download unavailable');
    }
  };

  // The archive is made in storage and the browser is sent there for it (lib/finalFiles.js): no time limit, a dropped
  // download resumes, and the prepared file is kept for a day. The same follower as the video Download All.
  const handleDownloadAll = async () => {
    if (zipJob?.status === 'working') return;
    const run = Math.abs(zipRun.current) + 1;
    zipRun.current = run;
    const mine = () => zipRun.current === run;
    let last = null;
    const how = await followZip({
      post: (url, body) => api.post(url, body), get: (url) => api.get(url),
      // An anchor download, never a blob: a multi-GB archive must not pass through the page's memory.
      go: (to) => { const a = document.createElement('a'); a.href = `${api.defaults.baseURL}${to}`; document.body.appendChild(a); a.click(); a.remove(); },
      wait: (ms) => new Promise(r => setTimeout(r, ms)),
      onJob: (job) => { last = job; if (mine()) setZipJob(job); },
      projectId: project.id, version: project.current_version || 1, alive: mine,
    });
    if (!mine()) return;
    if (how === 'started') {
      toast.success(ZIP_STARTED, { duration: 12000 });
      setTimeout(() => { if (mine()) setZipJob(null); }, 4000);
    } else if (how === 'error') {
      toast.error(zipLine(last) || 'The download could not be prepared. Try the individual downloads.');
      setZipJob(null);
    }
  };

  return (
    <div className="pt-3 border-t border-white/[0.04] space-y-2" data-testid="photo-deliverables-section">
      <div className="flex items-center justify-between">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Source Files</p>
        <div className="flex items-center gap-2">
          {deliverables.length > 1 && (isUploader || (isClient && !clientLocked)) && (
            <button onClick={handleDownloadAll} disabled={zipJob?.status === 'working'}
              data-testid="source-files-zip-btn"
              className="tap-auto flex items-center gap-1 font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/70 border border-gold-leaf/20 hover:bg-gold-leaf/10 rounded px-2 py-1 transition-colors disabled:opacity-50">
              {zipJob?.status === 'working'
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Zipping…</>
                : <><Archive className="w-3 h-3" /> Download all</>}
            </button>
          )}
          <span className="font-sans text-[8px] text-slate-500">Catalog · RAW · Sidecars</span>
        </div>
      </div>

      {zipJob?.status === 'working' && (
        <p className="text-[9px] text-slate-400" data-testid="source-files-zip-progress">
          {zipLine(zipJob)}
          {zipJob.failed_files?.length > 0 && ` · ${zipJob.failed_files.length} unavailable`}
        </p>
      )}

      {clientLocked && deliverables.length > 0 && (
        <p className="flex items-center gap-1.5 text-[9px] text-slate-500" data-testid="deliverables-locked-note">
          <Lock className="w-3 h-3" /> Available to download once you approve your photos.
        </p>
      )}

      {deliverables.length > 0 ? (
        <div className="space-y-1.5">
          {deliverables.map((f) => (
            <div key={f.file_id} className="flex items-center justify-between p-2 rounded-md bg-ug-surface/50 border border-ug-border-subtle" data-testid="deliverable-row">
              <span className="font-mono text-[10px] text-zinc-300 truncate mr-2">{f.filename}</span>
              <div className="flex items-center gap-1 shrink-0">
                {(isUploader || (isClient && !clientLocked)) && (
                  <button onClick={() => handleDownload(f)} data-testid="deliverable-download-btn"
                    className="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:text-ug-gold hover:bg-white/5 transition-colors" title="Download">
                    <Download className="w-3.5 h-3.5" />
                  </button>
                )}
                {isUploader && (
                  <button onClick={() => handleDelete(f.file_id)} data-testid="deliverable-delete-btn"
                    className="flex items-center justify-center w-6 h-6 rounded text-velvet-burgundy hover:bg-velvet-burgundy/10 transition-colors" title="Remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        isUploader && <p className="text-[9px] text-slate-600">No source files attached yet.</p>
      )}

      {isUploader && (
        <>
          <input ref={inputRef} type="file" multiple accept={ALLOWED_EXTS.join(',')} className="hidden"
            onChange={(e) => e.target.files?.length && handleFiles(e.target.files)} data-testid="deliverable-file-input" />
          <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="add-deliverable-btn"
            className="w-full flex items-center justify-center gap-2 p-2.5 rounded-md border border-dashed border-ug-border text-[10px] font-mono uppercase tracking-wider text-slate-400 hover:text-ug-gold hover:border-ug-gold/30 transition-colors disabled:opacity-50">
            {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> Add Deliverable Files</>}
          </button>
        </>
      )}
    </div>
  );
};
